import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild
} from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, finalize, fromEvent, Subscription } from 'rxjs';
import { INDIA_STATES_TAMIL } from '@/data/states';
import { INDIA_STATE_DISTRICTS } from '@/data/state-districts';
import Swal from 'sweetalert2';
import { ActivatedRoute } from '@angular/router';
import { EventBookingDetails } from '@/modals/event-details/eventBookingDetails';

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.scss']
})
export class RegistrationComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('pageHeader', { static: true })
    pageHeader!: ElementRef<HTMLElement>;

    @ViewChild('bgWrapper', { static: true })
    bgWrapper!: ElementRef<HTMLElement>;

    statesList = INDIA_STATES_TAMIL;
    districtList: any[] = [];
    currentYear = new Date().getFullYear();
    resizeSub?: Subscription;

    getEventCity: string = "";
    eventDetails: EventBookingDetails;
    canShow: boolean = false;

    remainingCount: number = 0;

    private skipUpdateRows = false;
    constructor(
        private fb: FormBuilder,
        private appService: AppService,
        private spinner: NgxSpinnerService,
        private toastr: ToastrService,
        private renderer: Renderer2,
        private route: ActivatedRoute
    ) { }

    // ---------------- FORM INITIALIZATION ----------------
    eventForm = this.fb.group({
        emailId: ['', [Validators.required, Validators.email]],
        name: ['', Validators.required],
        phoneNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
        whatsappNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
        state: ['', Validators.required],
        address: ['', Validators.required],
        district: ['', Validators.required],
        age: ['', Validators.required],
        physically: ['', Validators.required],
        ticketCount: [0],
        participants: [0, Validators.required],
        noScam: [false, Validators.requiredTrue],
        willBring: [false],
        willAttend: [''],
        eventId: [0],
        participantInputs: this.fb.array([])
    });

    get members() {
        return this.eventForm.get('participantInputs') as FormArray;
    }

    createMemberGroup() {
        return this.fb.group({
            participantName: ['', [Validators.required, Validators.minLength(3)]],
            whatsappNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]]
        });
    }

    // ------------------- ngOnInit -------------------
    ngOnInit() {

        // 1️⃣ Load encrypted event ID
        this.route.queryParams.subscribe((params) => {
            const cityValues = params['city'] || params[""];
            if (cityValues) {
                this.getEventCity = cityValues;
                this.getEventDetailsCity();
            }
        });

        // 2️⃣ Disable all main fields at start (except participants)
        Object.keys(this.eventForm.controls).forEach((key) => {
            if (key !== 'noScam' && key !== 'participants') {
                this.eventForm.get(key)?.disable({ emitEvent: false });
            }
        });

        // 3️⃣ Enable all fields after noScam is checked
        this.eventForm.get('noScam')?.valueChanges.subscribe((checked: boolean) => {
            if (checked) {

                // Enable all controls
                Object.keys(this.eventForm.controls).forEach((key) => {
                    this.eventForm.get(key)?.enable({ emitEvent: false });
                });

                // IMPORTANT: Always enable FormArray
                this.members.enable({ emitEvent: false });

            } else {

                // Disable main fields except: noScam, participants, participantInputs
                Object.keys(this.eventForm.controls).forEach((key) => {
                    if (key !== 'noScam' && key !== 'participants' && key !== 'participantInputs') {
                        this.eventForm.get(key)?.disable({ emitEvent: false });
                    }
                });

                // IMPORTANT: Keep FormArray enabled
                this.members.enable({ emitEvent: false });
            }
        });

        // 4️⃣ Auto copy phone → whatsapp
        this.eventForm.get('phoneNumber')?.valueChanges.subscribe((value) => {
            if (value && value.length === 10) {
                this.eventForm.get('whatsappNumber')?.setValue(value, { emitEvent: false });
            }
        });

        // 5️⃣ Update participant rows
        this.eventForm.get('participants')?.valueChanges.subscribe((count) => {
            if (this.skipUpdateRows) return;   // 🚀 NEW: cancel update during submit

            this.updateMemberRows(+count);
        });

        // 6️⃣ State → District
        this.eventForm.get('state')?.valueChanges.subscribe((stateValue) => {
            this.loadDistricts(stateValue);
        });
    }

    // ---------------- HEADER MARGIN ADJUST ----------------
    ngAfterViewInit() {
        this.adjustContentMargin();
        this.resizeSub = fromEvent(window, 'resize')
            .pipe(debounceTime(120))
            .subscribe(() => this.adjustContentMargin());
    }

    adjustContentMargin() {
        const headerEl = this.pageHeader?.nativeElement;
        const wrapperEl = this.bgWrapper?.nativeElement;
        if (headerEl && wrapperEl) {
            const height = headerEl.offsetHeight;
            this.renderer.setStyle(wrapperEl, 'margin-top', `${height}px`);
        }
    }

    // ------------------- HELPERS -------------------
    loadDistricts(stateValue: string) {
        this.districtList = INDIA_STATE_DISTRICTS[stateValue] || [];
        this.eventForm.get('district')?.setValue('');
    }

    allowOnlyNumbers(event: any) {
        event.target.value = event.target.value.replace(/[^0-9]/g, '');
    }

    updateMemberRows(count: number) {
        const existing = this.members.length;

        if (existing === count) return;

        if (existing > count) {
            for (let i = existing - 1; i >= count; i--) {
                this.members.removeAt(i);
            }
            return;
        }

        for (let i = existing; i < count; i++) {
            this.members.push(this.createMemberGroup());
        }
    }

    // -------------------- SUBMIT --------------------
    submitForm() {

        if (this.eventForm.invalid) {
            this.eventForm.markAllAsTouched();
            return;
        }

        /** +91 attach */
        const phone = this.eventForm.get('whatsappNumber')?.value;
        if (phone && !phone.startsWith('91')) {
            this.eventForm.get('whatsappNumber')?.setValue('91' + phone, { emitEvent: false });
        }

        this.eventForm.get('eventId').setValue(Number(this.eventDetails?.eventId))
        const participantsCount = Number(this.eventForm.get('participants')?.value) || 0;
        this.eventForm.get('ticketCount')?.setValue(participantsCount + 1, { emitEvent: false });
        /** MAIN PARTICIPANT */
        const mainParticipant = {
            participantName: this.eventForm.get('name')?.value,
            whatsappNumber: this.eventForm.get('phoneNumber')?.value
        };

        /** ADDITIONAL PARTICIPANTS */
        const additionalParticipants = this.members.getRawValue();

        /*/!** MERGE — additional first, main last *!/
        const finalParticipants = [...additionalParticipants, mainParticipant];*/

        /** 🚀 STOP valueChanges from triggering updateMemberRows */
        this.skipUpdateRows = true;

        /** PATCH FINAL LIST */
        this.eventForm.patchValue({
            participantInputs: additionalParticipants
        });

        const finalFormData = this.eventForm.getRawValue();
        console.log("FINAL SUBMIT:", finalFormData);

        /** ENABLE AGAIN */
        this.skipUpdateRows = false;

        this.saveRegisteredDetails(finalFormData);
    }



    saveRegisteredDetails(data: any) {
        this.spinner.show();

        this.appService.saveEventRegisteredDetails(data)
            .pipe(finalize(() => this.spinner.hide()))
            .subscribe({
                next: (res) => {
                    Swal.fire({
                        icon: 'success',
                        title: 'பதிவு வெற்றிகரமாக சேமிக்கப்பட்டது!',
                        html: 'Registration Saved Successfully!',
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#28a745'
                    }).then(() => {
                        this.eventForm.reset();
                        window.location.reload();
                    });
                },
                error: () => {
                    this.toastr.error("Unable to save registration. Try again!");
                }
            });
    }


    get maxSelectable(): number {
        return this.remainingCount - 1; // Eg: remainingCount=3 → maxSelectable=2
    }

    getEventDetailsCity() {
        this.spinner.show();

        this.appService.getCityBasedEventDetails(this.getEventCity)
            .pipe(finalize(() => this.spinner.hide()))
            .subscribe({
                next: (data) => {
                    this.eventDetails = data;

                    console.log(this.eventDetails)

                    const totalTicket = this.eventDetails?.bookingCount;
                    const bookedTicket = this.eventDetails?.bookedCount;
                    this.remainingCount = totalTicket - bookedTicket;
                    // this.remainingCount = totalTicket - bookedTicket;
                    console.log(this.remainingCount)
                    this.checkEventConditions();  // your logic
                },

                error: (err) => {
                    const msg = err.error?.message || err.message;

                    // Show toast
                    this.toastr.error(msg);

                    // Check specific error message
                    if (msg === 'Event already completed') {
                        this.canShow = true;
                    }
                }
            });
    }



    checkEventConditions() {
        const now = Date.now();  // current timestamp
        const eventEnd = this.eventDetails.endDate; // from API
        const bookingCount = this.eventDetails.bookingCount;

        this.canShow = (now >= eventEnd) || (bookingCount === this.eventDetails.bookedCount);
    }


    onHeaderImageLoad(): void {
        // Small timeout ensures browser layout has stabilized (optional)
        setTimeout(() => {
            this.adjustContentMargin();
        }, 20);
    }

    ngOnDestroy() {
        this.resizeSub?.unsubscribe();
    }
}
